import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Search, User, School, Users as UsersIcon, GraduationCap, X, Loader2 } from 'lucide-react';
import EntityDetailModal from './global-search/EntityDetailModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MESP Global Quick Search — Feb 2026 (rich-modal rewrite).
 *
 * Top-bar palette that searches teachers, students, parents, and
 * classrooms in a single call (/api/global-search) and opens the
 * selected result in a deep-detail Modal — never via page redirect.
 *
 * Cross-entity links inside the modal (e.g. classrooms inside a
 * teacher card, parents inside a student card) push a new modal
 * onto a stack so the user can drill in/out without ever leaving
 * the search context.
 *
 * Cmd/Ctrl+K remains owned by `CommandPalette`; this component is
 * triggered by the top-bar Search button.
 */
export const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ]       = useState('');
  const [results, setResults] = useState({ teachers: [], students: [], parents: [], classrooms: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  // Modal stack: each entry is { kind, id }. We render only the TOP.
  const [modalStack, setModalStack] = useState([]);
  const inputRef = useRef(null);

  // ── Close-on-Escape only (Cmd/Ctrl+K belongs to CommandPalette) ──
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (modalStack.length > 0) setModalStack((s) => s.slice(0, -1));
        else setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, modalStack.length]);

  // ── Reset state on close ──
  useEffect(() => {
    if (!open) {
      setQ('');
      setResults({ teachers: [], students: [], parents: [], classrooms: [] });
      setLoading(false);
      setError(null);
      setModalStack([]);
    }
  }, [open]);

  // ── Debounced server-side search ──
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) {
      setResults({ teachers: [], students: [], parents: [], classrooms: [] });
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API_URL}/api/global-search`, {
          params: { q: term, limit: 8 },
          withCredentials: true,
        });
        if (cancelled) return;
        setResults({
          teachers:   r.data.teachers   || [],
          students:   r.data.students   || [],
          parents:    r.data.parents    || [],
          classrooms: r.data.classrooms || [],
        });
      } catch (err) {
        if (cancelled) return;
        setError(err?.response?.data?.detail || 'تعذّر تنفيذ البحث.');
        setResults({ teachers: [], students: [], parents: [], classrooms: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, open]);

  const openEntity = (kind, id) => {
    if (!kind || !id) return;
    setModalStack((s) => [...s, { kind, id }]);
  };
  const popEntity = () => setModalStack((s) => s.slice(0, -1));
  const closeAllModals = () => setModalStack([]);
  // Fully exit the search experience (used when a quick-action button
  // inside an entity modal navigates to a different route).
  const exitSearch = () => {
    setModalStack([]);
    setOpen(false);
  };

  const topEntity = modalStack[modalStack.length - 1] || null;
  const totalResults = results.teachers.length + results.students.length +
                       results.parents.length + results.classrooms.length;

  return (
    <>
      {/* Trigger buttons (mobile + desktop) */}
      <button
        type="button"
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        className="md:hidden inline-flex items-center justify-center p-2 text-[#9CB0CC] hover:text-white hover:bg-white/10 rounded-md transition-colors"
        data-testid="global-search-btn-mobile"
        title="بحث"
        aria-label="بحث"
      >
        <Search size={20} />
      </button>
      <button
        type="button"
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 text-sm text-[#5C6B8C] bg-[#F5F5F0] hover:bg-[#E8E8E0] rounded-md border border-[#E2E4E0] transition-colors"
        data-testid="global-search-btn"
        title="بحث شامل"
      >
        <Search size={14} />
        <span>بحث شامل</span>
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[120] bg-black/40 flex items-start justify-center p-4 pt-20"
          data-testid="global-search-modal"
          onClick={() => setOpen(false)}
          dir="rtl"
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full overflow-hidden shadow-2xl border border-[#E2E4E0]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Input row */}
            <div className="flex items-center gap-2 border-b border-[#E2E4E0] px-3 py-2.5">
              <Search size={16} className="text-[#5C6B8C] flex-shrink-0" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="ابحث عن معلم، طالب، ولي أمر، أو قاعة..."
                className="flex-1 outline-none text-sm bg-transparent text-[#0A1128]"
                data-testid="global-search-input"
              />
              {loading && <Loader2 size={14} className="animate-spin text-[#5C6B8C]" />}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1 hover:bg-[#F5F5F0] rounded-sm"
                data-testid="global-search-close"
                aria-label="إغلاق البحث"
              >
                <X size={14} className="text-[#5C6B8C]" />
              </button>
            </div>

            {/* Results */}
            <div className="max-h-[65vh] overflow-y-auto">
              {q.trim().length < 2 && (
                <div className="p-6 text-center text-xs text-[#5C6B8C]" data-testid="global-search-hint">
                  اكتب حرفين على الأقل للبحث. النتائج تفتح في نافذة منبثقة مفصّلة.
                </div>
              )}

              {error && (
                <div className="p-6 text-center text-sm text-rose-700" data-testid="global-search-error">
                  {error}
                </div>
              )}

              {q.trim().length >= 2 && !loading && !error && totalResults === 0 && (
                <div className="p-6 text-center text-sm text-[#5C6B8C]" data-testid="global-search-empty">
                  لا توجد نتائج لـ &quot;{q}&quot;
                </div>
              )}

              <ResultGroup
                title="المعلمون"
                icon={GraduationCap}
                items={results.teachers}
                kind="teacher"
                onOpen={openEntity}
                renderMeta={(t) => [t.phone_number, t.email, t.status].filter(Boolean).join(' • ')}
              />
              <ResultGroup
                title="الطلاب"
                icon={User}
                items={results.students}
                kind="student"
                onOpen={openEntity}
                renderMeta={(s) => [s.phone_number, s.email, s.status].filter(Boolean).join(' • ')}
              />
              <ResultGroup
                title="أولياء الأمور"
                icon={UsersIcon}
                items={results.parents}
                kind="parent"
                onOpen={openEntity}
                renderMeta={(p) => [p.phone_number, p.email, p.status].filter(Boolean).join(' • ')}
              />
              <ResultGroup
                title="القاعات"
                icon={School}
                items={results.classrooms}
                kind="classroom"
                onOpen={openEntity}
                renderMeta={(c) => [c.classroom_code, c.teacher_name || '—', `${c.current_students || 0} طالب`, c.status].filter(Boolean).join(' • ')}
                primaryLabel={(c) => c.classroom_name || c.classroom_code}
              />
            </div>
          </div>

          {/* Stacked entity-detail modal (rendered ABOVE the palette via its own portal) */}
          {topEntity && (
            <EntityDetailModal
              entity={topEntity}
              onOpenEntity={openEntity}
              onBack={popEntity}
              onClose={closeAllModals}
              onNavigate={exitSearch}
              canGoBack={modalStack.length > 1}
            />
          )}
        </div>,
        document.body,
      )}
    </>
  );
};

/** Single result group renderer (used for all four entity kinds). */
const ResultGroup = ({ title, icon: Icon, items, kind, onOpen, renderMeta, primaryLabel }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="p-2 border-t border-[#E2E4E0] first:border-t-0" data-testid={`global-search-group-${kind}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#5C6B8C] px-2 pb-1 font-semibold">
        {title} ({items.length})
      </div>
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          onClick={() => onOpen(kind, it.id)}
          className="w-full flex items-center gap-3 px-3 py-2 text-start hover:bg-[#F5F5F0] rounded-sm transition-colors"
          data-testid={`global-search-result-${kind}-${it.id}`}
        >
          <Icon size={14} className="text-[#0A1128] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[#0A1128] truncate">
              {primaryLabel ? primaryLabel(it) : (it.full_name || '—')}
            </p>
            <p className="text-[11px] text-[#5C6B8C] truncate">{renderMeta(it) || '—'}</p>
          </div>
        </button>
      ))}
    </div>
  );
};

export default GlobalSearch;
